/**
 * Financial Projection Model Schema
 * 
 * Defines the complete data structure for year-by-year financial projections
 * including Revenue, EBITDA, initiative breakdowns, and assumptions.
 */

// ============================================================================
// CORE TYPES
// ============================================================================

export type FiscalYear = `FY${number}`; // e.g., "FY25", "FY26", "FY30"

export type CellEditSource = 'ai_generated' | 'user_edited' | 'calculated' | 'wizard_derived';

export interface CellMetadata {
    source: CellEditSource;
    editedAt?: string;
    editedBy?: string;
    formula?: string;
    confidence?: number; // 0-100
    notes?: string;
}

// ============================================================================
// INITIATIVE YEARLY BREAKDOWN
// ============================================================================

export interface InitiativeYearlyImpact {
    initiativeId: string;
    title: string;
    year: FiscalYear;
    
    // Revenue impact
    revenueImpact: number;
    revenueImpactMetadata: CellMetadata;
    
    // EBITDA impact (before OPEX deduction)
    grossEbitdaImpact: number;
    grossEbitdaImpactMetadata: CellMetadata;
    
    // Costs
    capex: number;
    capexMetadata: CellMetadata;
    capexBreakdown?: {
        item: string;
        amount: number;
        rationale: string;
    }[];
    
    opex: number;
    opexMetadata: CellMetadata;
    opexBreakdown?: {
        category: string;
        amount: number;
        rationale: string;
    }[];
    
    // Headcount
    headcountAdded: number;
    keyHires?: {
        role: string;
        annualCost: number;
        startYear: FiscalYear;
    }[];
    
    // Status
    status: 'planned' | 'in_progress' | 'completed' | 'cancelled';
    rampUpPercentage: number; // 0-100, representing % of full potential achieved
}

// ============================================================================
// YEARLY FINANCIALS
// ============================================================================

export interface YearlyAssumptions {
    organicGrowthRate: number; // Decimal, e.g., 0.10 for 10%
    organicGrowthRateMetadata: CellMetadata;
    
    ebitdaMarginPercent: number; // Decimal, e.g., 0.12 for 12%
    ebitdaMarginPercentMetadata: CellMetadata;
}

export interface YearlyRevenue {
    year: FiscalYear;
    
    // Business Plan Target (user-defined or AI-interpolated)
    businessPlanTarget: number;
    businessPlanTargetMetadata: CellMetadata;
    
    // Organic Growth = Total[Y-1] × OrganicGrowthRate[Y]
    organicGrowth: number;
    organicGrowthMetadata: CellMetadata;
    
    // Strategy Impact = Sum of all initiative revenue impacts
    strategyImpact: number;
    strategyImpactMetadata: CellMetadata;
    
    // Total = Total[Y-1] + OrganicGrowth + StrategyImpact
    total: number;
    totalMetadata: CellMetadata;
    
    // Gap = BusinessPlanTarget - Total (negative = exceeded)
    gapToTarget: number;
    gapToTargetMetadata: CellMetadata;
    
    // Breakdown by initiative
    initiativeBreakdown: InitiativeYearlyImpact[];
}

export interface YearlyEbitda {
    year: FiscalYear;
    
    // Business Plan Target
    businessPlanTarget: number;
    businessPlanTargetMetadata: CellMetadata;
    
    // Organic Growth = Revenue.Total × EBITDAMargin - EBITDA.Total[Y-1]
    organicGrowth: number;
    organicGrowthMetadata: CellMetadata;
    
    // Gross Strategy Impact (before costs)
    grossStrategyImpact: number;
    grossStrategyImpactMetadata: CellMetadata;
    
    // Total EBITDA = EBITDA.Total[Y-1] + OrganicGrowth + GrossStrategyImpact
    totalEbitda: number;
    totalEbitdaMetadata: CellMetadata;
    
    // Initiative CAPEX (below the line)
    totalCapex: number;
    totalCapexMetadata: CellMetadata;
    
    // Initiative OPEX (recurring costs)
    totalOpex: number;
    totalOpexMetadata: CellMetadata;
    
    // Net Cash Position = Total EBITDA - CAPEX - OPEX
    netCashPosition: number;
    netCashPositionMetadata: CellMetadata;
    
    // Gap = BusinessPlanTarget - Total EBITDA (negative = exceeded)
    gapToTarget: number;
    gapToTargetMetadata: CellMetadata;
    
    // Breakdown by initiative
    initiativeBreakdown: InitiativeYearlyImpact[];
}

export interface YearlyFinancials {
    year: FiscalYear;
    isBaseYear: boolean; // True for FY25 (actuals)
    isProjectedYear: boolean; // True for future years
    
    assumptions: YearlyAssumptions;
    revenue: YearlyRevenue;
    ebitda: YearlyEbitda;
}

// ============================================================================
// SCENARIO MODELING
// ============================================================================

export interface Scenario {
    id: string;
    name: string;
    description?: string;
    isDefault: boolean;
    createdAt: string;
    updatedAt: string;
    createdBy?: string;
    
    // Each scenario has its own set of projections
    projections: YearlyFinancials[];
}

// ============================================================================
// MAIN PROJECTION MODEL
// ============================================================================

export interface GlobalAssumptions {
    defaultOrganicGrowthRate: number; // Used when adding new years
    defaultEbitdaMargin: number;
    discountRate: number; // For NPV calculations
    inflationRate: number;
    avgSalaryByRole: Record<string, number>; // Role -> Annual cost
    currency: string;
    reportingUnit: 'absolute' | 'k' | 'M' | 'B' | 'crore';
}

export interface FinancialProjectionModel {
    id: string;
    companyId: string;
    
    // Year range
    startYear: FiscalYear;
    endYear: FiscalYear;
    baseYear: FiscalYear; // The year with actual data (usually current year)
    
    // Global settings
    globalAssumptions: GlobalAssumptions;
    
    // All scenarios
    scenarios: Scenario[];
    activeScenarioId: string;
    
    // Initiatives (shared across scenarios, impacts can differ)
    initiatives: {
        id: string;
        title: string;
        description: string;
        opportunityTitle: string;
        status: 'planned' | 'in_progress' | 'completed' | 'cancelled';
        startYear: FiscalYear;
        endYear?: FiscalYear;
    }[];
    
    // Metadata
    lastUpdatedAt: string;
    lastUpdatedBy?: string;
    supervisorValidatedAt?: string;
    supervisorValidationScore?: number;
    supervisorIssues?: string[];
    
    // Validation change tracking — populated by the Validate button, cleared on next user edit
    lastValidationChanges?: Array<{
        year: string;
        field: string;
        oldValue: number;
        newValue: number;
        reason: string;
    }>;
    
    // UI state
    collapsedYears: FiscalYear[]; // Years hidden between expanded years
    expandedInitiatives: string[]; // Initiative IDs that are expanded in the table
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Parse a fiscal year string to get the numeric year
 */
export function parseFiscalYear(fy: FiscalYear): number {
    const match = fy.match(/FY(\d+)/);
    if (!match) throw new Error(`Invalid fiscal year format: ${fy}`);
    const num = parseInt(match[1], 10);
    // Handle 2-digit vs 4-digit years
    return num < 100 ? 2000 + num : num;
}

/**
 * Create a fiscal year string from a numeric year
 */
export function toFiscalYear(year: number): FiscalYear {
    const fy = year >= 2000 ? year - 2000 : year;
    return `FY${fy}` as FiscalYear;
}

/**
 * Get the next fiscal year
 */
export function nextFiscalYear(fy: FiscalYear): FiscalYear {
    return toFiscalYear(parseFiscalYear(fy) + 1);
}

/**
 * Get the previous fiscal year
 */
export function prevFiscalYear(fy: FiscalYear): FiscalYear {
    return toFiscalYear(parseFiscalYear(fy) - 1);
}

/**
 * Generate an array of fiscal years between start and end (inclusive)
 */
export function generateFiscalYearRange(start: FiscalYear, end: FiscalYear): FiscalYear[] {
    const startNum = parseFiscalYear(start);
    const endNum = parseFiscalYear(end);
    const years: FiscalYear[] = [];
    
    for (let y = startNum; y <= endNum; y++) {
        years.push(toFiscalYear(y));
    }
    
    return years;
}

/**
 * Create default cell metadata for AI-generated values
 */
export function createAIMetadata(confidence: number = 80, formula?: string): CellMetadata {
    return {
        source: 'ai_generated',
        confidence,
        formula,
        editedAt: new Date().toISOString()
    };
}

/**
 * Create default cell metadata for user-edited values
 */
export function createUserEditMetadata(userId?: string): CellMetadata {
    return {
        source: 'user_edited',
        editedAt: new Date().toISOString(),
        editedBy: userId,
        confidence: 100
    };
}

/**
 * Create default cell metadata for calculated values
 */
export function createCalculatedMetadata(formula: string): CellMetadata {
    return {
        source: 'calculated',
        formula,
        confidence: 100
    };
}

/**
 * Create empty yearly financials for a given year
 */
export function createEmptyYearlyFinancials(year: FiscalYear, isBaseYear: boolean = false): YearlyFinancials {
    const defaultMetadata = createCalculatedMetadata('');
    
    return {
        year,
        isBaseYear,
        isProjectedYear: !isBaseYear,
        assumptions: {
            organicGrowthRate: 0.10,
            organicGrowthRateMetadata: createAIMetadata(80),
            ebitdaMarginPercent: 0.12,
            ebitdaMarginPercentMetadata: createAIMetadata(80)
        },
        revenue: {
            year,
            businessPlanTarget: 0,
            businessPlanTargetMetadata: defaultMetadata,
            organicGrowth: 0,
            organicGrowthMetadata: defaultMetadata,
            strategyImpact: 0,
            strategyImpactMetadata: defaultMetadata,
            total: 0,
            totalMetadata: defaultMetadata,
            gapToTarget: 0,
            gapToTargetMetadata: defaultMetadata,
            initiativeBreakdown: []
        },
        ebitda: {
            year,
            businessPlanTarget: 0,
            businessPlanTargetMetadata: defaultMetadata,
            organicGrowth: 0,
            organicGrowthMetadata: defaultMetadata,
            grossStrategyImpact: 0,
            grossStrategyImpactMetadata: defaultMetadata,
            totalEbitda: 0,
            totalEbitdaMetadata: defaultMetadata,
            totalCapex: 0,
            totalCapexMetadata: defaultMetadata,
            totalOpex: 0,
            totalOpexMetadata: defaultMetadata,
            netCashPosition: 0,
            netCashPositionMetadata: defaultMetadata,
            gapToTarget: 0,
            gapToTargetMetadata: defaultMetadata,
            initiativeBreakdown: []
        }
    };
}

/**
 * Create a default scenario
 */
export function createDefaultScenario(startYear: FiscalYear, endYear: FiscalYear): Scenario {
    const years = generateFiscalYearRange(startYear, endYear);
    
    return {
        id: 'base-case',
        name: 'Base Case',
        description: 'Default planning scenario',
        isDefault: true,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        projections: years.map((year, index) => 
            createEmptyYearlyFinancials(year, index === 0)
        )
    };
}

/**
 * Create a new empty projection model
 */
export function createEmptyProjectionModel(
    companyId: string,
    startYear: FiscalYear = 'FY25',
    endYear: FiscalYear = 'FY30',
    currency: string = 'USD',
    reportingUnit: 'absolute' | 'k' | 'M' | 'B' | 'crore' = 'M'
): FinancialProjectionModel {
    const defaultScenario = createDefaultScenario(startYear, endYear);
    
    return {
        id: `proj-${Date.now()}`,
        companyId,
        startYear,
        endYear,
        baseYear: startYear,
        globalAssumptions: {
            defaultOrganicGrowthRate: 0.10,
            defaultEbitdaMargin: 0.12,
            discountRate: 0.10,
            inflationRate: 0.03,
            avgSalaryByRole: {
                'Software Engineer': 150000,
                'Product Manager': 160000,
                'Sales Representative': 120000,
                'Marketing Manager': 130000,
                'Operations': 100000,
                'Executive': 250000
            },
            currency,
            reportingUnit
        },
        scenarios: [defaultScenario],
        activeScenarioId: defaultScenario.id,
        initiatives: [],
        lastUpdatedAt: new Date().toISOString(),
        collapsedYears: [],
        expandedInitiatives: []
    };
}
